<template>
  <div class="crowded-payout-account form-block mb-4">
    <h5 v-if="showTitle" class="tw-mb-2">
      {{ $t("gateways.crowded_payout_title", "Payouts") }}
    </h5>
    <crowded-balance-stages
      v-if="balance"
      variant="tiles"
      :balance="balance"
      :payout-account="hasAccount ? account : null"
    />
    <p v-if="!loading && !hasAccount" class="tw-text-sm tw-text-[#6f7a8a] tw-mb-3">
      {{
        $t(
          "gateways.crowded_payout_help",
          "Add the bank account where released donation funds should be automatically deposited each day."
        )
      }}
    </p>

    <div v-if="loading" class="tw-py-4 tw-text-center">
      <b-spinner small />
    </div>

    <error v-else-if="errors.length" :errors="errors" />

    <!-- Saved account -->
    <div v-if="!loading && hasAccount">
      <dl class="row tw-mb-3">
        <dt class="col-5 col-lg-4">
          {{ $t("gateways.crowded_payout_account_holder", "Account holder") }}
        </dt>
        <dd class="col-7 col-lg-8">{{ account.name }}</dd>

        <dt class="col-5 col-lg-4">
          {{ $t("gateways.crowded_payout_bank_account", "Bank account") }}
        </dt>
        <dd class="col-7 col-lg-8">
          {{ account.account_type }} ••{{ account.account_last4 }} ·
          {{ $t("gateways.crowded_payout_routing", "Routing") }}
          {{ account.routing_number }}
        </dd>

        <dt class="col-5 col-lg-4">{{ $t("gateways.status") }}</dt>
        <dd class="col-7 col-lg-8">
          <span :class="account.enabled ? 'tw-text-green-600' : 'tw-text-[#b98900]'">
            {{
              account.enabled
                ? $t("gateways.crowded_payout_enabled", "Automatic payouts on")
                : $t("gateways.crowded_payout_paused", "Paused")
            }}
          </span>
        </dd>

        <template v-if="account.last_payout_at">
          <dt class="col-5 col-lg-4">{{ $t("gateways.crowded_payout_last", "Last payout") }}</dt>
          <dd class="col-7 col-lg-8">
            {{ formatMoney(account.last_payout_amount_cents) }}
            <span class="tw-text-[#6f7a8a]">
              · {{ formatDate(account.last_payout_at) }} · {{ account.last_payout_status }}
            </span>
          </dd>
        </template>

        <template v-if="account.cumulative_paid_out_cents > 0">
          <dt class="col-5 col-lg-4">
            {{ $t("gateways.crowded_payout_total", "Total paid out") }}
          </dt>
          <dd class="col-7 col-lg-8">{{ formatMoney(account.cumulative_paid_out_cents) }}</dd>
        </template>
      </dl>

      <div class="tw-flex tw-flex-wrap tw-gap-2">
        <button
          type="button"
          class="bttn bttn--sm bttn--blue"
          :disabled="sending"
          @click="startEdit"
        >
          {{ $t("gateways.crowded_payout_replace", "Replace account") }}
        </button>
        <button
          type="button"
          class="bttn bttn--sm bttn--grey"
          :disabled="sending"
          @click="toggleEnabled"
        >
          {{
            account.enabled
              ? $t("gateways.crowded_payout_pause", "Pause payouts")
              : $t("gateways.crowded_payout_resume", "Resume payouts")
          }}
        </button>
        <button type="button" class="bttn bttn--sm bttn--red" :disabled="sending" @click="remove">
          {{ $t("common.remove", "Remove") }}
        </button>
      </div>

      <div class="crowded-payout-manual tw-mt-4 tw-pt-3">
        <h6 class="tw-mb-2">
          {{ $t("gateways.crowded_payout_manual_title", "Create a payout") }}
        </h6>
        <p class="tw-text-sm tw-text-[#6f7a8a] tw-mb-2">
          {{
            account.enabled
              ? $t(
                  "gateways.crowded_payout_automatic_info",
                  "Payouts will be generated daily automatically once they are available, and will be sent via standard ACH. Usual time to deposit is 1-3 business days. To manage payouts manually, pause the automatic payouts."
                )
              : $t(
                  "gateways.crowded_payout_paused_info",
                  "Automatic payouts are paused. Create a payout whenever you want to move funds to your bank account."
                )
          }}
        </p>
        <!-- span wrapper: a disabled button fires no mouse events, so it can't host the tooltip -->
        <span
          v-b-tooltip.hover
          class="d-inline-block"
          :title="
            account.manual_payout_allowed
              ? ''
              : $t(
                  'gateways.crowded_payout_manual_disabled_tooltip',
                  'Pause automatic payouts to create a payout.'
                )
          "
        >
          <button
            type="button"
            class="bttn bttn--sm bttn--orange"
            :disabled="sending || !account.manual_payout_allowed"
            @click="openManualPayout()"
          >
            {{ $t("gateways.crowded_payout_manual_button", "Create a payout") }}
          </button>
        </span>
      </div>
    </div>

    <!-- Add / replace form -->
    <form v-else-if="!loading" class="crowded-payout-form" @submit.prevent="save">
      <div v-if="!approved" class="alert alert-warning tw-text-sm">
        {{
          $t(
            "gateways.crowded_payout_needs_approval",
            "Your Crowded bank application must be approved before you can add a payout account."
          )
        }}
      </div>

      <div class="row">
        <div class="col-12 col-lg-6">
          <div class="form-group">
            <label class="form-group__label form-group__label--required">
              {{ $t("gateways.crowded_payout_account_holder", "Account holder") }}
            </label>
            <input
              v-model.trim="form.name"
              type="text"
              class="form-control"
              required
              maxlength="255"
            />
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="form-group">
            <label class="form-group__label form-group__label--required">
              {{ $t("gateways.crowded_payout_entity_type", "Account holder type") }}
            </label>
            <b-form-select v-model="form.entity_type" :options="entityTypeOptions" required />
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="form-group">
            <label class="form-group__label form-group__label--required">
              {{ $t("gateways.crowded_payout_routing", "Routing number") }}
            </label>
            <input
              v-model.trim="form.routing_number"
              type="text"
              inputmode="numeric"
              class="form-control"
              required
              maxlength="9"
              @input="form.routing_number = digitsOnly(form.routing_number, 9)"
            />
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="form-group">
            <label class="form-group__label form-group__label--required">
              {{ $t("gateways.crowded_payout_account_number", "Account number") }}
            </label>
            <input
              v-model.trim="form.account_number"
              type="text"
              inputmode="numeric"
              class="form-control"
              required
              maxlength="17"
              @input="form.account_number = digitsOnly(form.account_number, 17)"
            />
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="form-group">
            <label class="form-group__label form-group__label--required">
              {{ $t("gateways.crowded_payout_account_type", "Account type") }}
            </label>
            <b-form-select v-model="form.account_type" :options="accountTypeOptions" required />
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="form-group">
            <label class="form-group__label">
              {{ $t("gateways.crowded_payout_nickname", "Nickname (optional)") }}
            </label>
            <input v-model.trim="form.nickname" type="text" class="form-control" maxlength="128" />
          </div>
        </div>
      </div>

      <p class="tw-text-xs tw-text-[#6f7a8a] tw-mb-3">
        {{
          $t(
            "gateways.crowded_payout_authorization",
            "By saving, you authorize Charidy to initiate ACH credit transfers of your released funds to this account."
          )
        }}
      </p>

      <div class="tw-flex tw-gap-2">
        <button
          type="submit"
          class="bttn bttn--sm bttn--orange"
          :class="{ disabled: sending || !approved }"
          :disabled="sending || !approved"
        >
          {{ $t("gateways.save", "Save") }}
        </button>
        <button
          v-if="editingExisting"
          type="button"
          class="bttn bttn--sm bttn--grey"
          :disabled="sending"
          @click="cancelEdit"
        >
          {{ $t("common.cancel", "Cancel") }}
        </button>
      </div>
    </form>

    <p v-if="!loading && showCrowdedNote" class="tw-text-xs tw-text-[#6f7a8a] tw-mt-3 tw-mb-0">
      {{
        $t(
          "gateways.crowded_manage_directly",
          "To manage your account directly on Crowded, visit {url} and enter the phone number associated with your account.",
          { url: crowdedAppUrl }
        )
      }}
    </p>

    <crowded-manual-payout ref="manualPayout" :gateway-id="gatewayId" @created="fetch" />
  </div>
</template>

<script>
import axios from "axios";
import urls from "@/constants/urls.js";
import error from "@/components/errors.vue";
import { CROWDED_APP_URL } from "@/constants/crowded.js";
import CrowdedManualPayout from "./crowdedManualPayout.vue";
import CrowdedBalanceStages from "./crowdedBalanceStages.vue";

const emptyForm = () => ({
  name: "",
  entity_type: "Business",
  routing_number: "",
  account_number: "",
  account_type: "Checking",
  nickname: "",
});

export default {
  name: "CrowdedPayoutAccount",

  components: { error, CrowdedManualPayout, CrowdedBalanceStages },

  props: {
    // The base "crowded" gateway (organization_merchant_info id) whose payout
    // account this is; every request names it.
    gatewayId: { type: [Number, String], required: true },
    approved: { type: Boolean, default: false },
    showTitle: { type: Boolean, default: true },
    // Off where the host already links to Crowded (the gateway details view).
    showCrowdedNote: { type: Boolean, default: true },
    // Gateway balance snapshot; when set, the Processing / Releasing soon /
    // Available tiles are shown above the account.
    balance: { type: Object, default: null },
  },

  data() {
    return {
      loading: true,
      sending: false,
      errors: [],
      account: null,
      editingExisting: false,
      form: emptyForm(),
      crowdedAppUrl: CROWDED_APP_URL,
      entityTypeOptions: [
        { value: "Business", text: "Business" },
        { value: "Person", text: "Person" },
      ],
      accountTypeOptions: [
        { value: "Checking", text: "Checking" },
        { value: "Savings", text: "Savings" },
      ],
    };
  },

  computed: {
    orgId() {
      return localStorage.getItem("orgId");
    },
    endpoint() {
      return urls.crowdedPayoutAccount.replace(":oid", this.orgId);
    },
    requestConfig() {
      return { params: { gateway_id: this.gatewayId } };
    },
    hasAccount() {
      return !this.editingExisting && this.account && this.account.status !== "missing";
    },
  },

  created() {
    this.fetch();
  },

  methods: {
    digitsOnly(v, max) {
      return (v || "").replace(/\D/g, "").slice(0, max);
    },

    formatMoney(cents) {
      const n = Number(cents || 0) / 100;
      return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
    },

    formatDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleString();
    },

    fetch() {
      this.loading = true;
      this.$store
        .dispatch("fetchCrowdedPayoutAccount", this.gatewayId)
        .then((data) => {
          this.account = data;
          this.$emit("updated", data);
        })
        .catch(this.showError)
        .finally(() => {
          this.loading = false;
        });
    },

    // Keeps the gateways list header (payout status) in step with this panel.
    setAccount(data) {
      this.account = data;
      this.$store.commit("setCrowdedPayoutAccount", { gatewayId: this.gatewayId, account: data });
      this.$emit("updated", data);
    },

    openManualPayout(options) {
      return this.$refs.manualPayout.open(options);
    },

    startEdit() {
      this.form = emptyForm();
      if (this.account) {
        this.form.name = this.account.name || "";
        this.form.entity_type = this.account.entity_type || "Business";
        this.form.account_type = this.account.account_type || "Checking";
        this.form.nickname = this.account.nickname || "";
      }
      this.editingExisting = true;
    },

    cancelEdit() {
      this.editingExisting = false;
      this.errors = [];
    },

    save() {
      this.errors = [];
      this.sending = true;
      axios
        .post(this.endpoint, this.form, this.requestConfig)
        .then(({ data }) => {
          this.setAccount(data);
          this.editingExisting = false;
          this.form = emptyForm();
          this.$_notificationsMixin_makeToastCompat &&
            this.$_notificationsMixin_makeToastCompat({
              group: "Notification",
              title: this.$t("gateways.crowded_payout_saved", "Payout account saved"),
            });
          // Offer to pay out the existing balance now; it stays closed when
          // there is nothing to pay out.
          if (data.initial_payout_window) {
            this.openManualPayout({ initial: true });
          }
        })
        .catch(this.showError)
        .finally(() => {
          this.sending = false;
        });
    },

    toggleEnabled() {
      this.errors = [];
      this.sending = true;
      axios
        .patch(this.endpoint, { enabled: !this.account.enabled }, this.requestConfig)
        .then(({ data }) => {
          this.setAccount(data);
        })
        .catch(this.showError)
        .finally(() => {
          this.sending = false;
        });
    },

    remove() {
      if (
        !window.confirm(
          this.$t(
            "gateways.crowded_payout_remove_confirm",
            "Remove this payout account? Automatic payouts will stop."
          )
        )
      ) {
        return;
      }
      this.errors = [];
      this.sending = true;
      axios
        .delete(this.endpoint, this.requestConfig)
        .then(() => {
          this.setAccount({ status: "missing" });
          this.editingExisting = false;
        })
        .catch(this.showError)
        .finally(() => {
          this.sending = false;
        });
    },

    showError(e) {
      if (!e || !e.response) {
        this.errors.push(this.$t("template.error_default", "Something went wrong"));
        return;
      }
      const d = e.response.data;
      if (d && d.error) {
        this.errors.push(d.error);
      } else if (d && Array.isArray(d.errors)) {
        this.errors = d.errors.map((err) => err.detail || err.title).filter(Boolean);
      } else {
        this.errors.push(this.$t("template.error_default", "Something went wrong"));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.crowded-payout-account {
  dt {
    font-family: $vito-bold;
    color: $fd;
  }
}

.crowded-payout-manual {
  border-top: 1px solid #e6eaed;
}
</style>
